import { CSSProperties, ReactNode } from 'react';

export interface SegmentedControlOption {
  value: string;
  label: ReactNode;
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value: string;
  onChange: (value: string) => void;
  /** Accessible label for the control (e.g. "Sign in or register"). */
  'aria-label': string;
  /** 'default' (theme --line border, --text active) — or 'inverted': a
   *  quiet translucent-cream outline, for a fixed dark/violet backdrop that
   *  doesn't track the app's light/dark theme (e.g. auth screens). Kept
   *  deliberately understated in both tones — a segmented control is a
   *  navigational choice, not a CTA, so it shouldn't compete with one. */
  tone?: 'default' | 'inverted';
  style?: CSSProperties;
}

/**
 * SegmentedControl — a compact two-or-more-way toggle in a single bordered
 * track (unlike Tab, which is separate floating pills). Use for mutually
 * exclusive modes shown together, e.g. "Sign in / Register".
 */
export function SegmentedControl({ options, value, onChange, tone = 'default', style, 'aria-label': ariaLabel }: SegmentedControlProps) {
  const inverted = tone === 'inverted';
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        border: `1px solid ${inverted ? 'rgba(255, 252, 219, .22)' : 'var(--line)'}`,
        borderRadius: 'var(--r-pill)',
        padding: 3,
        ...style,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            style={{
              border: 'none',
              borderRadius: 'var(--r-pill)',
              padding: '7px 20px',
              fontFamily: 'var(--font-sans)',
              fontSize: 12.5,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              transition: 'background .15s, color .15s',
              background: active ? (inverted ? 'rgba(255, 252, 219, .16)' : 'var(--nav-active-bg)') : 'transparent',
              color: active ? (inverted ? 'rgb(var(--cream-bright))' : 'var(--text)') : (inverted ? 'rgba(255, 252, 219, .5)' : 'var(--text-soft)'),
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
